import { useState, useCallback, useEffect } from 'react';
import {
  initializePaperEvaluationChat,
  sendPaperEvaluationChatMessage,
  getPaperEvaluationChatHistory,
  PaperEvaluationChatMessage,
  PaperEvaluationChatResponse,
} from '../api';
import { WorkData } from '../types';

export type PaperEvaluationChatState = {
  chatId: string | null;
  messages: PaperEvaluationChatMessage[];
  loading: boolean;
  error: string | null;
};

export type PaperEvaluationChatActions = {
  clearError: () => void;
  resetChat: () => void;
  sendMessage: (message: string) => Promise<void>;
};

export const usePaperEvaluationChat = (
  work?: WorkData,
  initialChatId?: string,
): PaperEvaluationChatState & PaperEvaluationChatActions => {
  const [chatId, setChatId] = useState<string | null>(initialChatId || null);
  const [messages, setMessages] = useState<PaperEvaluationChatMessage[] | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleApiPromiseResponse = useCallback(
    (response: Promise<PaperEvaluationChatResponse>) => {
      return response
        .then((response) => {
          setChatId(response.chat_id);
          setMessages(response.conversation);
          setLoading(false);
          setError(null);
        })
        .catch((error) => {
          setError(error.message);
          setLoading(false);
        });
    },
    [],
  );

  const sendMessage = useCallback(
    (message: string): Promise<void> => {
      if (!chatId) {
        setError('No active chat');
        return Promise.reject(new Error('No active chat'));
      }

      return handleApiPromiseResponse(
        sendPaperEvaluationChatMessage(chatId, message),
      );
    },
    [chatId, handleApiPromiseResponse],
  );

  const loadChatHistory = useCallback(
    (chatId: string): Promise<void> => {
      return handleApiPromiseResponse(getPaperEvaluationChatHistory(chatId));
    },
    [handleApiPromiseResponse],
  );

  const initializeChat = useCallback(
    async (openalexWorkId: string): Promise<void> => {
      return handleApiPromiseResponse(
        initializePaperEvaluationChat(openalexWorkId),
      );
    },
    [handleApiPromiseResponse],
  );

  // using messagesNull to avoid calling this circularly
  const messagesNull = messages === null;
  useEffect(() => {
    if (!messagesNull) {
      return;
    } else if (chatId) {
      loadChatHistory(chatId);
    } else if (work?.work.id && work.work.workType === 'openalex_work') {
      initializeChat(work.work.id);
    } else {
      setError('No chat id or openalex work id provided');
    }
  }, [
    loadChatHistory,
    initializeChat,
    work?.work.id,
    work?.work.workType,
    messagesNull,
    chatId,
  ]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetChat = useCallback(() => {
    setChatId(null);
    setMessages(null);
    setLoading(false);
    setError(null);
  }, []);

  return {
    chatId,
    messages: messages || [],
    loading,
    error,

    sendMessage,
    clearError,
    resetChat,
  };
};
